import React, { useState } from 'react';
import { Button, Persona, Text, Tooltip, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import { ReactComponent as Pinned } from '../../../assets/Pinned.svg';
import { ReactComponent as Unpinned } from '../../../assets/Unpin.svg';
import { customColors, customFonts } from '../../../styles';

import useRecentlyUsedAgents from '../hooks/useRecentlyUsedAgents';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { buildAvatarSrc } from '../../utils/AvatarUtils';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: '100%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
        ...shorthands.padding('10px', '0px', '10px', '20px'),
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        userSelect: 'none',
        flexShrink: 0,
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
    },
    headerToggle: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        color: customColors.white,
        fontWeight: 500,
        fontSize: tokens.fontSizeBase200,
        fontFamily: 'inherit',
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        ...shorthands.padding('0px'),
        cursor: 'pointer',
        textAlign: 'left',
    },
    headerActions: {
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
    },
    headerActionButton: {
        minWidth: '44px',
        width: '44px',
        color: customColors.white,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        ...shorthands.padding('0px'),
        flexShrink: 0,
        cursor: 'pointer',
    },
    agentList: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('2px'),
    },
    agentItem: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.padding('8px', '20px'),
        cursor: 'pointer',
        ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
    },
    agentItemDisabled: {
        cursor: 'not-allowed',
        opacity: 0.6,
        ':hover': {
            backgroundColor: 'transparent',
        },
    },
    agentAvatar: {
        flexShrink: 0,
        '& > span': {
            justifyContent: 'center',
            alignItems: 'center',
            display: 'inline-flex',
        },
        '& > span > img': {
            backgroundColor: 'transparent',
            width: '100%',
            height: '100%',
        },
    },
    agentName: {
        marginLeft: tokens.spacingHorizontalS,
        fontSize: '12.5px',
        fontFamily: customFonts.Lato,
        color: customColors.white,
        ...shorthands.overflow('hidden'),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        flexGrow: 1,
        minWidth: 0,
    },
    pinButton: {
        marginLeft: 'auto',
        minWidth: 'auto',
        ...shorthands.padding('4px'),
        color: customColors.white,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        flexShrink: 0,
        ':hover': {
            backgroundColor: 'transparent',
            color: 'rgba(255, 255, 255, 0.7)',
        },
    },
    pinIcon: {
        width: '16px',
        height: '16px',
    },
    emptyState: {
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: tokens.fontSizeBase200,
        fontFamily: customFonts.Lato,
        ...shorthands.padding('8px', '20px'),
    },
});

const ChatListRecentlyUsed: React.FC = () => {
    const styles = useStyles();
    const { agents, isMaxChatsReached, togglePin, createAgentChat } = useRecentlyUsedAgents();
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <button className={styles.headerToggle} onClick={() => setIsExpanded((prev) => !prev)}>
                    <span>Recently Used Agents</span>
                </button>
                <div className={styles.headerActions}>
                    <button
                        className={styles.headerActionButton}
                        aria-label={isExpanded ? 'Collapse recently used agents' : 'Expand recently used agents'}
                        onClick={() => setIsExpanded((prev) => !prev)}
                    >
                        {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <div className={styles.agentList}>
                    {agents.length === 0 && <Text className={styles.emptyState}>No recent agents</Text>}
                    {agents.map((agent) => {
                        const avatarSrc = buildAvatarSrc(ChatProviderType.Nexus, agent.agentIconUrl);
                        const agentRow = (
                            <div
                                className={`${styles.agentItem} ${isMaxChatsReached ? styles.agentItemDisabled : ''}`}
                                onClick={() => {
                                    if (isMaxChatsReached) {
                                        return;
                                    }

                                    void createAgentChat(agent.agentId);
                                }}
                                data-disabled={isMaxChatsReached ? 'true' : undefined}
                            >
                                <Persona
                                    className={styles.agentAvatar}
                                    avatar={{
                                        image: { src: avatarSrc },
                                        shape: 'square',
                                        size: 36,
                                    }}
                                />
                                <Text className={styles.agentName}>{agent.name}</Text>
                                <Tooltip content={agent.isPinned ? 'Unpin agent' : 'Pin agent'} relationship="label">
                                    <Button
                                        appearance="transparent"
                                        className={styles.pinButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            togglePin(agent.agentId);
                                        }}
                                        icon={
                                            agent.isPinned ? (
                                                <Pinned className={styles.pinIcon} />
                                            ) : (
                                                <Unpinned className={styles.pinIcon} />
                                            )
                                        }
                                    />
                                </Tooltip>
                            </div>
                        );

                        if (isMaxChatsReached) {
                            return (
                                <Tooltip
                                    key={agent.agentId}
                                    content="Max active chat sessions reached. Please delete a chat to create a new one."
                                    relationship="label"
                                >
                                    {agentRow}
                                </Tooltip>
                            );
                        }

                        return <React.Fragment key={agent.agentId}>{agentRow}</React.Fragment>;
                    })}
                </div>
            )}
        </div>
    );
};

export default ChatListRecentlyUsed;
